import { bus } from '../core/bus';
import { S, hasGame } from '../core/game';
import { dayId } from '../core/state';
import { clock, m, onLangChange } from '../i18n';
import { currentObjective, FORCE_SLEEP } from '../content/days';
import { unreadCount } from '../phone/phone';
import type { World } from '../world/world';
import { $ } from './dom';
import { compass } from '../content/treasure';
import { dateLabel } from '../content/calendar';
import { WEATHER_ICON, weatherNow } from '../content/weather';
import { mapName } from '../i18n/labels';
import { questText, step, isActive, questList, type QuestId } from '../content/quests';
import { currentPin } from '../content/guide';

/** Writes only when a value actually changed, so the HUD costs ~nothing per frame. */
const cache = new Map<string, string>();
function text(id: string, v: string): void {
  if (cache.get(id) === v) return;
  cache.set(id, v);
  $(id).textContent = v;
}
function attr(id: string, key: string, v: string): void {
  const k = `${id}@${key}`;
  if (cache.get(k) === v) return;
  cache.set(k, v);
  const el = $(id);
  if (key === 'class') el.className = v;
  else if (key === '--v') el.style.setProperty('--v', v);
  else el.setAttribute(key, v);
}

let world: World | null = null;
let lastPlace = '';
let placeTimer = 0;
let placeArmed = false;

export function initHud(w: World): void {
  world = w;
  bus.on('change', update);
  onLangChange(() => {
    cache.clear();
    labels();
    update();
  });
  labels();
}

/** Static labels (tooltips). */
function labels(): void {
  const t = m();
  const set = (id: string, label: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.setAttribute('aria-label', label);
    el.setAttribute('title', label);
  };
  set('timebox', t.side.todo);
  set('btnJournal', t.side.journal);
  set('btnBag', t.side.bag);
  set('btnMap', t.side.map);
  set('btnPhone', t.side.phone);
  set('btnMenu', t.side.menu);
  set('barEnergy', t.hud.energy);
  set('barMood', t.hud.mood);
  set('barStudy', t.hud.study);
  set('barBladder', t.hud.bladder);
  $('ctxBtn').setAttribute('aria-label', t.common.gotIt);
}

/** The place card only announces places once the game is actually running. */
export function armPlaceCard(on: boolean): void {
  placeArmed = on;
  if (!on) lastPlace = '';
}

function showPlace(mapLabel: string, zone: string): void {
  text('placeMap', mapLabel);
  text('placeName', zone);
  const card = $('placeCard');
  card.classList.add('show');
  window.clearTimeout(placeTimer);
  placeTimer = window.setTimeout(() => card.classList.remove('show'), 2600);
}

/** The line in the quest bar: the tracked quest's current step, else today's next goal. */
function questLine(): { text: string; done: boolean } {
  const s = S();
  const t = m();
  const pin = currentPin();
  if (pin) return { text: t.airport.mapUi.guiding(pin.label), done: false };
  const tracked = s.tracked as QuestId | null;
  if (tracked && isActive(tracked)) {
    const q = questText(tracked);
    const st = Math.max(0, step(tracked));
    return { text: q.steps[Math.min(st, q.steps.length - 1)], done: false };
  }
  const obj = currentObjective(s);
  if (obj) return { text: obj, done: false };
  return { text: t.objectives.allDone, done: true };
}

export function update(): void {
  if (!hasGame()) return;
  const s = S();
  const d = m().days[dayId(s)];
  // Date, weekday and the weather (sky package): "Fri, Sep 4" · ☀️ · 31°C ☂10%.
  const wx = weatherNow();
  const date = dateLabel();
  text('hudDay', date);
  text('hudTime', clock(s.time));
  text('hudWxIcon', WEATHER_ICON[wx.kind]);
  text('hudTemp', `${wx.temp}°C`);
  text('hudRain', `☂ ${wx.rainPct}%`);
  attr('hudRain', 'class', wx.rainPct >= 60 ? 'rp hi' : 'rp');
  attr('timebox', 'class', wx.kind === 'typhoon' ? 'clock wx-typhoon' : 'clock');
  attr('timebox', 'title', m().sky.hud.title(s.week, `${date} (${d.name})`, m().sky.kinds[wx.kind]));
  const left = Math.max(0, FORCE_SLEEP - s.time);
  const zone = world ? world.area() : '';
  // The countdown only matters in the evening; before that the location is more useful.
  text('hudLoc', s.time >= 19 * 60 ? `🌙 ${m().hud.untilBed(Math.floor(left / 60), String(Math.floor(left % 60)).padStart(2, '0'))}` : zone);
  // Cinematic card when the area changes.
  if (world && placeArmed) {
    const key = `${world.map.id}:${zone}`;
    if (key !== lastPlace) {
      const first = lastPlace === '';
      lastPlace = key;
      if (!first && zone) {
        const mapLabel = mapName(world.map.id);
        showPlace(mapLabel === zone ? '' : mapLabel, zone);
      }
    }
  }
  const bars: [string, number, boolean][] = [
    ['Energy', s.stats.energy, s.stats.energy < 20],
    ['Mood', s.stats.mood, s.stats.mood < 20],
    ['Study', s.stats.study, false],
    ['Bladder', s.stats.bladder, s.stats.bladder > 75],
  ];
  for (const [k, v, warn] of bars) {
    attr(`bar${k}`, '--v', v.toFixed(0));
    text(`val${k}`, String(Math.round(v)));
    attr(`bar${k}`, 'class', `gauge ${k.toLowerCase()}${warn ? ' warn' : ''}`);
  }
  text('hudStars', String(s.stars));
  text('hudCoins', String(s.coins));
  const q = questLine();
  text('taskTitle', q.text);
  attr('taskBar', 'class', q.done ? 'quest-bar done' : 'quest-bar');
  const unread = unreadCount();
  text('phBadge', String(unread));
  attr('phBadge', 'class', unread ? 'badge' : 'badge hidden');
  const offers = questList(s).filter((x) => x.state === 'available').length;
  text('qBadge', String(offers));
  attr('qBadge', 'class', offers ? 'badge' : 'badge hidden');
  const near = world?.near;
  text('ctxBtn', near ? (near.kind === 'npc' ? '💬' : near.kind === 'spot' ? '⛏️' : near.obj.icon) : '✋');
  // Treasure compass (only while a treasure is being tracked).
  const cmp = world ? compass(world.map.id, world.player.x, world.player.y) : null;
  text('compass', cmp ? (cmp.text.startsWith('🧭') ? cmp.text : `🏴‍☠️ ${cmp.text}`) : '');
  attr('compass', 'class', cmp ? `compass heat${cmp.level}` : 'compass hidden');
  attr('ctxBtn', 'class', near ? 'ctx ready' : 'ctx');
}

/** Called a few times per second for the ticking clock. */
export function hudTick(): void {
  update();
}
